import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackendConnector from '../Utils/BackendConnector';
import Constant from '../Utils/Constant';
import Error500Page from '../Error500/Error500Page';
import NotFoundPage from '../NotFound/NotFoundPage';
import { FaArrowLeft, FaMapMarkerAlt, FaRegCalendar } from 'react-icons/fa';
import { formatRange, sortStages, stageDays, stageNights, tripFacts } from './itinerary';

const TravelPage = () => {
    const { countryId } = useParams();
    const navigate = useNavigate();
    const [country, setCountry] = useState(null);
    const [places, setPlaces] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);

    useEffect(() => {
        const fetchTravelData = async () => {
            try {
                setIsLoading(true);
                
                // Récupération des détails du pays
                const countryResponse = await BackendConnector.fetchData('travels/');
                const countryData = countryResponse.find(c => c.id === countryId);
                
                if (!countryData) {
                    throw new Error(Constant.COUNTRY_NOT_FOUND);
                }
                
                setCountry(countryData);
                
                // Récupération des places du pays
                const placesData = await BackendConnector.fetchData(`travels/${countryId}/places/`);
                setPlaces(placesData);
                
            } catch (err) {
                console.error('Erreur lors du chargement du voyage:', err);
                
                // Extraire le statut d'erreur du message d'erreur
                const statusMatch = err.message.match(/status: (\d+)/);
                const status = statusMatch ? parseInt(statusMatch[1]) : null;
                
                setErrorStatus(status);
                setError(err.message || Constant.UNABLE_TO_LOAD_TRAVEL);
            } finally {
                setIsLoading(false);
            }
        };

        if (countryId) {
            fetchTravelData();
        }
    }, [countryId]);

    const handleBackClick = () => {
        navigate('/');
        setTimeout(() => {
            const travelsElement = document.getElementById('travels');
            if (travelsElement) {
                travelsElement.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    const handlePlaceClick = (placeId) => {
        navigate(`/travel/${countryId}/place/${placeId}`);
    };



    if (isLoading) {
        return (
            <div className="not-found-page">
                <div className="not-found-content">
                    <div className="spinner-border text-light" role="status" style={{fontSize: '2rem'}}>
                        <span className="visually-hidden">{Constant.LOADING}</span>
                    </div>
                    <p className="not-found-message mt-3">{Constant.LOADING_TRAVEL}</p>
                </div>
            </div>
        );
    }

    if (error || !country) {
        // Si c'est une erreur 500, afficher la page d'erreur 500
        if (errorStatus === 500) {
            return <Error500Page />;
        }
        
        // Sinon, afficher la page 404 (pour les erreurs 404 ou autres)
        let userMessage = Constant.TRAVEL_NOT_FOUND;
        let technicalMessage = null;
        if (error && (error.includes('404') || error.toLowerCase().includes('not found'))) {
            userMessage = Constant.TRAVEL_NOT_FOUND;
            technicalMessage = null;
        } else if (error) {
            userMessage = error;
            technicalMessage = error;
        }
        return (
            <NotFoundPage
                title={"404"}
                message={Constant.ERROR}
                description={userMessage}
                technicalMessage={technicalMessage}
                buttonLabel={Constant.BACK_TO_TRAVELS}
                onBack={handleBackClick}
                iconClass="fas fa-arrow-left me-2"
            />
        );
    }

    const stages = sortStages(places);
    const facts = tripFacts(places);

    return (
        <div className="travel-page">
            {/* En-tête : photo, pays et présentation du voyage */}
            <header
                className="travel-hero"
                style={{
                    backgroundImage: country.cover ? `url(${country.cover})` : 'none'
                }}
            >
                <div className="travel-hero-inner">
                    <button className="travel-back" onClick={handleBackClick}>
                        <FaArrowLeft aria-hidden="true" /> {Constant.BACK_TO_TRAVELS}
                    </button>

                    <div className="travel-hero-content">
                        <h1 className="travel-page-title">
                            <span className="travel-flag" aria-hidden="true">
                                {country.flag || Constant.DEFAULT_COUNTRY_FLAG}
                            </span>
                            {country.name}
                        </h1>
                        {country.summary && (
                            <p className="travel-lead">{country.summary}</p>
                        )}
                    </div>
                </div>
            </header>

            {/* Le voyage en chiffres, à cheval sur la photo */}
            {facts && (
                <section className="travel-facts" aria-label={Constant.TRIP_FACTS}>
                    <dl className="travel-facts-list">
                        <div className="travel-fact">
                            <dt>{Constant.TRIP_STAGES}</dt>
                            <dd>{facts.stages}</dd>
                        </div>
                        <div className="travel-fact">
                            <dt>{Constant.TRIP_DAYS}</dt>
                            <dd>{facts.days}</dd>
                        </div>
                        <div className="travel-fact travel-fact-period">
                            <dt>{Constant.TRIP_PERIOD}</dt>
                            <dd>{formatRange(facts.start, facts.end)}</dd>
                        </div>
                    </dl>
                </section>
            )}

            {/* Itinéraire : les étapes dans l'ordre, reliées par un fil */}
            <main className="travel-body">
                <h2 className="travel-section-title">{Constant.ITINERARY}</h2>

                {stages.length === 0 ? (
                    <p className="travel-empty">{Constant.NO_PLACES_MESSAGE}</p>
                ) : (
                    <ol className="travel-stages">
                        {stages.map((place, index) => {
                            const nights = stageNights(place);
                            const href = `/travel/${countryId}/place/${place.id}`;
                            return (
                                <li key={place.id} className="travel-stage">
                                    <span className="travel-stage-number" aria-hidden="true">{index + 1}</span>
                                    <a
                                        className="travel-stage-card"
                                        href={href}
                                        onClick={(e) => { e.preventDefault(); handlePlaceClick(place.id); }}
                                    >
                                        <div className="travel-stage-image">
                                            {place.cover ? (
                                                <img src={place.cover} alt="" loading="lazy" />
                                            ) : (
                                                <span className="travel-stage-placeholder">
                                                    <FaMapMarkerAlt aria-hidden="true" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="travel-stage-body">
                                            <p className="travel-stage-days">{stageDays(place, facts.start)}</p>
                                            <h3 className="travel-stage-title">{place.name}</h3>
                                            <p className="travel-stage-dates">
                                                <FaRegCalendar aria-hidden="true" />
                                                {formatRange(place.start_date, place.end_date)}
                                                {nights && <span className="travel-stage-nights">{nights}</span>}
                                            </p>
                                            {place.summary && (
                                                <p className="travel-stage-summary">{place.summary}</p>
                                            )}
                                            <span className="travel-stage-link">{Constant.SEE_STAGE}</span>
                                        </div>
                                    </a>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </main>
        </div>
    );
};

export default TravelPage;